import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import currentTimeZone from '../../../../config/timeZone';
import { useAppSelector } from '../../../../redux/hooks';
import CustomRadioButton from '../../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import CustomTimelineChart from '../../../../shared-components/custom-timeline-chart/CustomTimelineChart';
import HeatMapCalendar from '../../../../shared-components/heatmap-calendar/HeatMapCalendar';
import {
  interpolateColorThreeStops,
  reduceColorOpacity,
} from '../../../../shared-functions/ColorFunctions';
import getCurrentTimeInLocalISO from '../../../../shared-functions/CurrentTimeInLocalISO';
import { convertDecimalHoursToTime } from '../../../../shared-functions/DateTime';
import capitalizeFirstLetter from '../../../../shared-functions/StringFunctions';
import device from '../../../../utils/api/device';

interface DateStatusProperties {
  id: string;
  date: number;
  backgroundColor: string;
  borderColor: string;
  [key: string]: string | number;
}

interface ConnectorStatusProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: 'Preparing' | 'Charging' | 'Finishing' | string; // Add other statuses if any
  startsAt: string; // ISO date string
  endsAt: string; // ISO date string
  startsAtAdjusted: string;
  endsAtAdjusted: string;
}

interface DeviceStatusProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: 'Preparing' | 'Charging' | 'Finishing' | string; // Add other statuses if any
  startsAt: string; // ISO date string
  endsAt: string; // ISO date string
  startsAtAdjusted: string;
  endsAtAdjusted: string;
  transactionStartsAtAdjusted: string;
  transactionEndsAtAdjusted: string;
  // PowerConsumption Data
  meterStart?: number; // Optional, if not available
  meterStop?: number; // Optional, if not available
  powerConsumed?: number; // Optional, if not available
  // Connectivity
  onlineHours?: number; // Optional, if not available
  offlineHours?: number; // Optional, if not available
}

interface ChargingPointConnectorStatusProperties {
  _id: number;
  connectorStatus: ConnectorStatusProperties[];
}

/**
 * Format date in local time as YYYY-MM-DD
 */
const DateFormatter = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Function to process device status data and calc active hours effectively
 */
const processDeviceStatusRawData = (
  month: number,
  year: number,
  rawData: DeviceStatusProperties[],
) => {
  // Start of the target month
  // const monthStartAt = new Date(year, month - 1, 1, 0, 0, 0, 0);

  // Filter data for the specified month and year, and handle null endsAtAdjusted
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);

  const processedData = rawData.map((item) => {
    let startDate = new Date(item.startsAtAdjusted);
    let endDate = item.endsAtAdjusted
      ? new Date(item.endsAtAdjusted)
      : new Date(getCurrentTimeInLocalISO());

    // Clamp to today’s end if the date is in the future
    const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
    const usedFallback = !item.endsAtAdjusted;

    if (isCurrentMonth && (usedFallback || endDate > todayEnd)) {
      endDate = todayEnd;
    }

    // Clamp start date if before the month
    const monthStartAt = new Date(year, month - 1, 1, 0, 0, 0, 0);
    if (startDate < monthStartAt) {
      startDate = monthStartAt;
    }

    return {
      ...item,
      startsAtAdjusted: startDate,
      endsAtAdjusted: endDate,
    };
  });

  // Sort by start time
  processedData.sort((a, b) => a.startsAtAdjusted.getTime() - b.startsAtAdjusted.getTime());

  // Initialize result object for each day in the month
  const result: Record<string, any> = {};

  const isCurrentMonth = today.getMonth() + 1 === month && today.getFullYear() === year;
  const daysInMonth = new Date(year, month, 0).getDate();
  const lastDay = isCurrentMonth ? today.getDate() : daysInMonth;

  for (let day = 1; day <= lastDay; day += 1) {
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    result[dateString] = {
      date: dateString,
      onlineHours: 0,
      offlineHours: 0,
      powerConsumed: 0,
    };
  }

  // Get the first and last moment of the target month
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);

  // Process each status period
  /* eslint-disable no-restricted-syntax */
  for (const item of processedData) {
    // Here each item contains meterStart and meterStop
    let start = item?.startsAtAdjusted;
    let end = item?.endsAtAdjusted;
    const status = item?.status;
    const meterStart = item?.meterStart || 0;
    const meterStop = item?.meterStop || 0;
    const powerDiff = meterStop - meterStart;

    // Adjust start and end to be within the target month
    start = start < monthStart ? monthStart : start;
    end = end > monthEnd ? monthEnd : end;

    // Process each day in the period
    const currentDay = new Date(start);
    currentDay.setHours(0, 0, 0, 0);
    while (currentDay <= end) {
      const dayStart = new Date(currentDay);
      const dayEnd = new Date(currentDay);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // Calculate overlap between status period and this day
      const overlapStart = new Date(Math.max(start.getTime(), dayStart.getTime()));
      const overlapEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));

      const hours = (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60);

      const dateString = `${year}-${month.toString().padStart(2, '0')}-${currentDay.getDate().toString().padStart(2, '0')}`;

      if (status === 'online' || status === 'Charging') {
        result[dateString].onlineHours = (result[dateString]?.onlineHours ?? 0) + hours;
      } else {
        result[dateString].offlineHours = (result[dateString]?.offlineHours ?? 0) + hours;
      }

      // Add power consumption only on the day when the period ends
      if (overlapEnd.getTime() === end.getTime() && currentDay.getDate() === end.getDate()) {
        result[dateString].powerConsumed = (result[dateString]?.powerConsumed ?? 0) + powerDiff;
      }

      currentDay.setDate(currentDay.getDate() + 1);
    }
  }

  // Convert to array and round hours to 2 decimal places
  return Object.values(result).map((day) => ({
    ...day,
    onlineHours: Math.round(day.onlineHours * 100) / 100,
    offlineHours: Math.round(day.offlineHours * 100) / 100,
  }));
};

/**
 * Fills gaps between charging transactions with "Not Charging" periods.
 *
 * @param transactions - Array of charging transactions
 * @param startTimeISO - ISO date string (start of the range, e.g., "2025-07-31T00:00:00Z")
 * @param endTimeISO - ISO date string (end of the range, e.g., current time)
 * @returns Array of transactions with gaps filled
 */
function fillDeviceConsumptionDailyGaps(
  transactions: DeviceStatusProperties[],
  startTimeISO: string,
  endTimeISO: string,
) {
  const sorted = [...transactions].sort(
    (a, b) =>
      new Date(a.transactionStartsAtAdjusted).getTime() -
      new Date(b.transactionStartsAtAdjusted).getTime(),
  );

  const filled: any = [];
  let pointer = new Date(startTimeISO);
  const endTime = new Date(endTimeISO);

  for (const txn of sorted) {
    const txnStart = new Date(txn.transactionStartsAtAdjusted);
    const txnEnd = new Date(txn.transactionEndsAtAdjusted);

    if (txnStart > pointer) {
      filled.push({
        _id: txn._id,
        transactionStartsAtAdjusted: pointer.toISOString(),
        transactionEndsAtAdjusted: txnStart.toISOString(),
        status: 'idle',
        chargingPointSn: txn?.chargingPointSn || '',
        connector: txn?.connector || 1,
      });
    }

    filled.push(txn);
    pointer = txnEnd > pointer ? txnEnd : pointer;
  }

  if (pointer < endTime) {
    filled.push({
      _id: 'Final Gap',
      transactionStartsAtAdjusted: pointer.toISOString(),
      transactionEndsAtAdjusted: endTime.toISOString(),
      status: 'idle',
    });
  }

  return filled;
}

/**
 * Device management timeline view
 * @returns
 */
function Timeline() {
  const { t, i18n } = useTranslation('devices');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const [dateStatus, setDateStatus] = useState<DateStatusProperties[]>([]);
  const [currentTab, setCurrentTab] = useState('consumption');
  const [timelineOrTableView, setTableOrTableView] = useState('timeline');

  const [timelineDataLoading, setTimelineDataLoading] = useState({
    consumption: false,
    connectivity: false,
    connectorStatus: false,
    heatMap: false,
  });

  const [heatMapMinMaxValues, setHeatMapMinMaxValues] = useState({
    minimum: 0,
    maximum: 0,
  });

  const [topTimelineData, setTopTimelineData] = useState<DeviceStatusProperties[]>([]);
  const [connectorStatus, setConnectorStatus] = useState<ChargingPointConnectorStatusProperties[]>(
    [],
  );

  const [monthAndYear, setMonthAndYear] = useState({
    month: selectedDate?.getMonth()?.toString(), // Using the already defined currentMonth
    year: selectedDate?.getFullYear()?.toString(), // Using the already defined currentYear
  });

  const consumptionColumn = [
    {
      label: t('date'),
      accessorKey: 'dateToShow' as keyof DateStatusProperties,
      sortAllow: false,
    },
    {
      label: t('activeHours'),
      accessorKey: 'onlineHours' as keyof DateStatusProperties,
      sortAllow: false,
      /**
       * Function to add hours
       * @param rowData
       * @returns
       */
      customData: (rowData: DateStatusProperties) => {
        const hoursDecimal = Number(rowData?.onlineHours || 0);

        const hours = Math.floor(hoursDecimal);
        const minutes = Math.round((hoursDecimal - hours) * 60);

        return (
          <p>
            {hours} hrs {minutes} mins
          </p>
        );
      },
    },
    {
      label: t('idleHours'),
      accessorKey: 'offlineHours' as keyof DateStatusProperties,
      sortAllow: false,
      /**
       * Function to add hours
       * @param rowData
       * @returns
       */
      customData: (rowData: DateStatusProperties) => {
        const hoursDecimal = Number(rowData?.offlineHours || 0);

        const hours = Math.floor(hoursDecimal);
        const minutes = Math.round((hoursDecimal - hours) * 60);

        return (
          <p>
            {hours} hrs {minutes} mins
          </p>
        );
      },
    },
  ];

  const connectivityColumn = [
    {
      label: t('date'),
      accessorKey: 'dateToShow' as keyof DateStatusProperties,
      sortAllow: false,
    },
    {
      label: t('onlineHours'),
      accessorKey: 'onlineHours' as keyof DateStatusProperties,
      sortAllow: false,
      /**
       * Function to add hours
       * @param rowData
       * @returns
       */
      customData: (rowData: DateStatusProperties) => <p>{rowData?.onlineHours}</p>,
    },
    {
      label: t('offlineHours'),
      accessorKey: 'offlineHours' as keyof DateStatusProperties,
      sortAllow: false,
      /**
       * Function to add hours
       * @param rowData
       * @returns
       */
      customData: (rowData: DateStatusProperties) => {
        const hoursDecimal = Number(rowData?.offlineHours || 0);

        const hours = Math.floor(hoursDecimal);
        const minutes = Math.round((hoursDecimal - hours) * 60);

        return (
          <p>
            {hours} hrs {minutes} mins
          </p>
        );
      },
    },
  ];

  /**
   * Function to update gradient colors for device status data
   * @param data
   * @param key
   * @returns
   */
  function assignGradientColors(
    data: DeviceStatusProperties[],
    type: 'connectivity' | 'consumption',
  ) {
    let values: number[] = [];
    if (type === 'consumption') {
      values = data.map((item) => Number(item?.powerConsumed || 0));
    }

    if (type === 'connectivity') {
      values = data.map((item) => Number(item?.onlineHours || 0));
    }

    let min = 0;
    if (type === 'consumption') {
      min = Math.min(...values);
    }

    const max = Math.max(...values);
    const range = max - min || 1; // Prevent division by zero

    // Update the min & max values to the state
    setHeatMapMinMaxValues({
      minimum: min,
      maximum: max,
    });

    return data.map((item) => {
      const value =
        type === 'consumption' ? Number(item?.powerConsumed || 0) : Number(item?.onlineHours || 0);
      const normalized = (value - min) / range;

      // Hue from red (0°) to green (120°)
      // const hue = 0 + normalized * 120;
      // const color = `hsl(${hue}, 100%, 50%)`;

      // Custom color code
      const color = interpolateColorThreeStops('#FF0101', '#FF5A0F', '#2E9E3A', normalized);

      return {
        ...item,
        color,
      };
    });
  }

  /**
   * Fetch calendar heatMap data month wise
   */
  const fetchConsumptionMonthWise = async (month: number, year: number) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: true,
    }));
    if (selectedChargingPoint?.chargingPointSn) {
      await device
        .fetchChargingPointConceptionMonthWise(selectedChargingPoint?.chargingPointSn, {
          month,
          year,
        })
        .then((response) => {
          const processedData = processDeviceStatusRawData(month, year, response?.data);
          const colorAssignedData = assignGradientColors(processedData, 'consumption');

          setDateStatus(
            colorAssignedData?.map((eachDate: any) => ({
              ...eachDate,
              dateToShow: eachDate?.date,
              date: Number(eachDate?.date?.split('-')[2]),
              backgroundColor: eachDate?.color
                ? reduceColorOpacity(eachDate?.color, 0.4)
                : 'Transparent',
              borderColor: eachDate?.color || '#FFF',
              powerConsumed:
                eachDate?.color && eachDate?.powerConsumed
                  ? `${(eachDate.powerConsumed / 1000).toFixed(2)} kW`
                  : '0 kW',
            })),
          );
        })
        .catch((error) => console.log(error));
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: false,
    }));
  };

  /**
   * Function to fetch timeline
   * @param date
   */
  const fetchConsumptionData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      consumption: true,
    }));

    const isFutureDate = date > new Date(); // Compare selected date with current date

    if (isFutureDate) {
      setTopTimelineData([]); // Set empty array for future dates
      setTimelineDataLoading((previous) => ({
        ...previous,
        consumption: false,
      }));
      return;
    }

    if (selectedChargingPoint?.chargingPointSn) {
      await device
        .fetchChargingPointConceptionByDate(selectedChargingPoint?.chargingPointSn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
        })
        .then((response) => {
          const isToday = DateFormatter(new Date()) === DateFormatter(date);

          const filledData =
            fillDeviceConsumptionDailyGaps(
              response?.data || [],
              `${DateFormatter(date)}T00:00:00Z`,
              isToday ? getCurrentTimeInLocalISO() : `${DateFormatter(date)}T23:59:59.000Z`,
            ) || [];

          setTopTimelineData(filledData);
        })
        .catch((error) => console.log(error));
    }

    // Reset loading state after fetching data
    setTimelineDataLoading((previous) => ({
      ...previous,
      consumption: false,
    }));
  };

  /**
   * Fetch connector status by when the selected date is changed
   * @param date
   */
  const fetchConnectorStatusData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectorStatus: true,
    }));

    const isFutureDate = date > new Date(); // Compare selected date with current date

    if (isFutureDate) {
      setTopTimelineData([]); // Set empty array for future dates
      setTimelineDataLoading((previous) => ({
        ...previous,
        connectorStatus: false,
      }));
      return;
    }

    if (selectedChargingPoint?.chargingPointSn) {
      await device
        .fetchConnectorStatusByDate(selectedChargingPoint?.chargingPointSn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
        })
        .then((response) => {
          setConnectorStatus(response?.data || []);
        })
        .catch((error) => console.log(error));
    }

    setTimelineDataLoading((previous) => ({
      ...previous,
      connectorStatus: false,
    }));
  };

  /**
   * Fetch connector status by when the selected date is changed
   * @param date
   */
  const fetchDeviceStatusData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectivity: true,
    }));
    if (selectedChargingPoint?.chargingPointSn) {
      await device
        .fetchDeviceStatusByDate(selectedChargingPoint?.chargingPointSn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
        })
        .then((response) => {
          setTopTimelineData(response?.data || []);
        })
        .catch((error) => console.log(error));
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectivity: false,
    }));
  };

  /**
   * Fetch calendar heatMap data month wise
   */
  const fetchDeviceStatusMonthWise = async (month: number, year: number) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: true,
    }));
    if (selectedChargingPoint?.chargingPointSn) {
      await device
        .fetchDeviceStatusMonthWise(selectedChargingPoint?.chargingPointSn, {
          month,
          year,
        })
        .then((response) => {
          const processedData = processDeviceStatusRawData(month, year, response?.data);
          const colorAssignedData = assignGradientColors(processedData, 'connectivity');

          setDateStatus(
            colorAssignedData?.map((eachDate: any) => ({
              ...eachDate,
              dateToShow: eachDate?.date,
              date: Number(eachDate?.date?.split('-')[2]),
              backgroundColor: eachDate?.color
                ? reduceColorOpacity(eachDate?.color, 0.4)
                : 'Transparent',
              borderColor: eachDate?.color || '#FFF',
              onlineHours: convertDecimalHoursToTime(eachDate?.onlineHours || 0),
            })),
          );
        })
        .catch((error) => console.log(error));
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: false,
    }));
  };

  /**
   * handle change radio button function
   * @param name
   * @param value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentTab(value);
  };

  /**
   * Function to fetch month wise data based on current tab and month/year selection
   */
  const fetchMonthWiseData = () => {
    if (currentTab === 'connectivity') {
      fetchDeviceStatusMonthWise(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    } else {
      fetchConsumptionMonthWise(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    }
  };

  /**
   * Function to fetch day wise data based on selected date and current tab
   */
  const fetchDayWiseData = () => {
    fetchConnectorStatusData(selectedDate);
    if (currentTab === 'connectivity') {
      fetchDeviceStatusData(selectedDate);
    } else {
      fetchConsumptionData(selectedDate);
    }
  };

  useEffect(() => {
    // Trigger fetch when `currentTab` changes
    fetchMonthWiseData();
  }, [currentTab]);

  useEffect(() => {
    // Trigger fetch when either date or tab changes
    fetchDayWiseData();
  }, [selectedDate, currentTab]);

  return (
    <div className="device-management-timeline-wrap" dir={i18n.dir()}>
      <div className="device-timeline-table-view-container">
        <div className="device-timeline-table-view-toggle-container">
          <CustomRadioButton
            name=""
            value="timeline"
            label={t('timeline')}
            selected={timelineOrTableView === 'timeline'}
            onChangeFunc={() => setTableOrTableView('timeline')}
            fontSize="small"
          />
          <CustomRadioButton
            name=""
            value="table"
            label={t('table')}
            selected={timelineOrTableView === 'table'}
            onChangeFunc={() => setTableOrTableView('table')}
            fontSize="small"
          />
        </div>
        {timelineOrTableView === 'timeline' ? (
          <div className="device-timeline-view-container">
            <div className="device-timeline-view-content">
              <CustomTimelineChart
                title={capitalizeFirstLetter(currentTab)}
                timelineData={
                  currentTab === 'consumption'
                    ? topTimelineData?.map((eachTimeline) => ({
                        start: eachTimeline?.transactionStartsAtAdjusted,
                        end: eachTimeline?.transactionEndsAtAdjusted,
                        status: eachTimeline?.status,
                      }))
                    : topTimelineData?.map((eachTimeline) => ({
                        start: eachTimeline?.startsAtAdjusted,
                        end: eachTimeline?.endsAtAdjusted,
                        status: eachTimeline?.status,
                      }))
                }
                timelineLabel={
                  currentTab === 'consumption'
                    ? [
                        {
                          name: t('powerConsumed'),
                          color: 'var(--primary-color)',
                          key: 'charging',
                        },
                        {
                          name: t('idle'),
                          color: '#B3261E',
                          key: 'idle',
                        },
                      ]
                    : [
                        {
                          name: t('online'),
                          color: 'green',
                          key: 'online',
                        },
                        {
                          name: t('offline'),
                          color: '#B3261E',
                          key: 'offline',
                        },
                      ]
                }
                date={DateFormatter(selectedDate)}
                dataLoading={
                  currentTab === 'consumption'
                    ? timelineDataLoading?.consumption
                    : timelineDataLoading?.connectivity
                }
                // defaultStatusToShow={currentTab === 'consumption' ? 'offline' : undefined}
              />
            </div>
            <div className="device-connector-status-wrap">
              {connectorStatus
                ?.filter((eachConnectorStatus) => eachConnectorStatus._id !== 0)
                ?.map((eachConnectorStatus, index) => (
                  <div className="each-connector-status-container">
                    <CustomTimelineChart
                      title={`Connector ${eachConnectorStatus?._id || index}`}
                      timelineData={eachConnectorStatus?.connectorStatus?.map((eachTimeline) => ({
                        start: eachTimeline?.startsAtAdjusted,
                        end: eachTimeline?.endsAtAdjusted,
                        status: eachTimeline?.status,
                      }))}
                      // defaultStatusToShow="faulted"
                      timelineLabel={[
                        {
                          name: t('available'),
                          color: '#6C757D',
                          key: 'available',
                        },
                        {
                          name: t('charging'),
                          color: '#2E9E3A',
                          key: 'charging',
                        },
                        {
                          name: t('preparing'),
                          color: '#F79009',
                          key: 'preparing',
                        },
                        {
                          name: t('finishing'),
                          color: '#00A79D',
                          key: 'finishing',
                        },
                        {
                          name: t('faulted'),
                          color: '#B3261E',
                          key: 'faulted',
                        },
                        {
                          name: t('unAvailable'),
                          color: '#1a2128',
                          key: 'unAvailable',
                        },
                      ]}
                      date={DateFormatter(selectedDate)}
                      dataLoading={timelineDataLoading?.connectorStatus}
                    />
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="device-table-view-container">
            <CustomTable
              header={currentTab === 'consumption' ? consumptionColumn : connectivityColumn}
              data={dateStatus}
              serialNo
              filter={{}}
              filterClearFunction={() => {}}
              filterFunction={() => {}}
              actionButton={false}
              limit={1}
              page={1}
              loading={false}
              totalPages={1}
              totalResults={1}
              pageLimitChange={(value: any) => {
                console.log(value);
              }}
              sizeChangeButtonRequired
              disablePagination
              disableHeader
              height={window.innerHeight >= 700 ? 347 : 352}
            />
          </div>
        )}
      </div>
      <div className="device-calendar-view-container">
        <div className="device-calendar-view-header">
          <div className="device-calendar-view-toggle-container">
            <CustomRadioButton
              name=""
              value="consumption"
              label={t('consumption')}
              selected={currentTab === 'consumption'}
              onChangeFunc={handleAvailabilityChange}
              fontSize="small"
            />
            <CustomRadioButton
              name=""
              value="connectivity"
              label={t('connectivity')}
              selected={currentTab === 'connectivity'}
              onChangeFunc={handleAvailabilityChange}
              fontSize="small"
            />
          </div>
          {currentTab === 'consumption' && (
            <div className="calendar-min-max-warp">
              <span className="calendar-value-min">
                {t('min')}: {((heatMapMinMaxValues?.minimum || 0) / 1000).toFixed(2)} kW
              </span>
              <span className="calendar-value-max">
                {t('max')}: {((heatMapMinMaxValues?.maximum || 0) / 1000).toFixed(2)} kW
              </span>
            </div>
          )}
        </div>
        <HeatMapCalendar
          dateStatusData={dateStatus}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          // handleMonthOrYearChange={fetchConsumptionMonthWise}
          handleMonthOrYearChange={
            currentTab === 'connectivity' ? fetchDeviceStatusMonthWise : fetchConsumptionMonthWise
          }
          monthAndYear={monthAndYear}
          setMonthAndYear={setMonthAndYear}
          keyToLookupUnderData={currentTab === 'consumption' ? 'powerConsumed' : 'onlineHours'}
          dataLoading={timelineDataLoading?.heatMap}
        />
      </div>
    </div>
  );
}

export default Timeline;
